import { useMemo } from "react";
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { TenantRead } from "@harmonyso/tenants-sdk";

interface TenantGrowthChartProps {
  tenants: TenantRead[];
}

export function TenantGrowthChart({ tenants }: TenantGrowthChartProps) {
  const chartData = useMemo(() => {
    if (!tenants.length) return [];

    // Sort tenants by creation date
    const sortedTenants = [...tenants].sort((a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    // Create cumulative data points
    const data: { period: string; total: number }[] = [];
    const monthlyTotals = new Map<string, number>();

    sortedTenants.forEach((tenant, index) => {
      const date = new Date(tenant.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const displayDate = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;

      // Update monthly total
      monthlyTotals.set(monthKey, index + 1);

      // Only add one point per month (the last one for that month)
      const isLastInMonth = index === sortedTenants.length - 1 ||
        new Date(sortedTenants[index + 1].created_at).getMonth() !== date.getMonth() ||
        new Date(sortedTenants[index + 1].created_at).getFullYear() !== date.getFullYear();

      if (isLastInMonth) {
        data.push({
          period: displayDate,
          total: index + 1,
        });
      }
    });

    return data;
  }, [tenants]);

  const chartConfig = {
    total: {
      label: "Total Tenants",
      color: "#60a5fa",
    },
  } satisfies ChartConfig;

  const totalTenants = tenants.length;
  const firstTenant = tenants.length > 0 ?
    new Date(Math.min(...tenants.map(t => new Date(t.created_at).getTime()))) :
    new Date();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base font-medium">Tenant Growth Over Time</CardTitle>
          <CardDescription>
            Cumulative tenant count from {firstTenant.toLocaleDateString()} to today ({totalTenants} total)
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <ChartContainer config={chartConfig} className="min-h-[200px] w-full">
          <LineChart accessibilityLayer data={chartData} margin={{ left: -20, right: 12, top: 12, bottom: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="period"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Line
              dataKey="total"
              type="monotone"
              stroke="var(--color-total)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}